import { CatalogSelector as OrigCatalogSelector } from "@hubrise/react-components"
import type { CatalogForSelector } from "@hubrise/react-components"
import * as React from "react"
import { useTranslation } from "react-i18next"

import { useConfig } from "shared/components/ConfigContext"
import { useEditorStoreContext } from "shared/components/EditorStoreContext"
import { useNavigationContext } from "shared/components/NavigationContext"
import { useCatalogSummariesQuery } from "shared/hooks/queries"
import useEditor from "shared/hooks/useEditor"
import useNavigateNoUpdate from "shared/hooks/useNavigateNoUpdate"
import { sortCatalogSummaries } from "shared/hubrise/utils/CatalogSummary"
import { catalogPath, DisplayMode } from "shared/utils/paths"

import Actions from "./Actions"
import { StyledCatalogSelector, Unsaved } from "./Styles"

const CatalogSelector = ({ displayMode }: { displayMode: DisplayMode }): JSX.Element => {
  const { t } = useTranslation()
  const { appInstanceId } = useConfig()

  const navigate = useNavigateNoUpdate()
  const { getCatalogNavigation } = useNavigationContext()

  const { data: catalogSummaries } = useCatalogSummariesQuery(appInstanceId)
  const { catalog } = useEditor()
  const { openEditors } = useEditorStoreContext()

  if (catalogSummaries === undefined) {
    return <StyledCatalogSelector />
  }

  const sortedCatalogSummaries = sortCatalogSummaries(catalogSummaries)
  const catalogs: Array<CatalogForSelector> = sortedCatalogSummaries.map((catalogSummary) => {
    const editor = openEditors[catalogSummary.id]
    const unsaved = editor !== undefined && editor.unsaved
    const path = (catalogId: string) =>
      getCatalogNavigation(catalogId)?.lastVisitedPath(displayMode) ?? catalogPath(catalogId, displayMode)

    return {
      id: catalogSummary.id,
      name: (
        <>
          {unsaved && <Unsaved>{t("general.unsaved_symbol")}</Unsaved>}
          {catalogSummary.name}
        </>
      ),
      onClick: () => navigate(path(catalogSummary.id)),
    }
  })

  return (
    <StyledCatalogSelector>
      <OrigCatalogSelector catalogs={catalogs} selectedCatalogId={catalog.hrId} variant="empty" />
      <Actions catalogSummaries={sortedCatalogSummaries} displayMode={displayMode} />
    </StyledCatalogSelector>
  )
}

export default CatalogSelector
